/**
 * Fold.jsx
 * A section that opens and closes, at two sizes: a whole app, or one part of
 * one.
 *
 * Built on <details>, not on state, for two reasons. The browser already knows
 * how to open and close one, including from the keyboard and for a screen
 * reader; and a closed <details> still holds its content, so Ctrl+F finds a
 * page name inside an app nobody has opened.
 *
 * A closed fold still has to be worth reading, so the summary carries the
 * figures — a row of apps, all shut, is the comparison.
 *
 * Dependencies: components/Icons.jsx
 */

import { Chevron } from '../Icons.jsx';

/**
 * One part of an app: its charts, its pages, its links.
 *
 * @param {string} title what the section is
 * @param {string} [note] the count or hint shown on the right while closed
 * @param {boolean} [defaultOpen] open before anybody touches it
 */
export function Fold({ title, note, defaultOpen = false, children }) {
  return (
    <details className="fold fold--sub" open={defaultOpen}>
      <summary className="fold__head">
        <Chevron size={16} />
        <span className="fold__title">{title}</span>
        {note && <span className="fold__note">{note}</span>}
      </summary>
      <div className="fold__body">{children}</div>
    </details>
  );
}

/**
 * A whole app.
 *
 * @param {object} head what to show whether it is open or shut: icon, name,
 *   host, the figures, and anything that needs attention
 */
export function AppFold({ icon, name, host, meta, defaultOpen = false, children }) {
  return (
    <details className="fold fold--app" open={defaultOpen}>
      <summary className="fold__head">
        <span className="fold__chev">
          <Chevron size={18} />
        </span>
        {icon}
        <span className="fold__name">
          <strong>{name}</strong>
          <span className="fold__host">{host}</span>
        </span>
        <span className="fold__meta">{meta}</span>
      </summary>
      <div className="fold__body">{children}</div>
    </details>
  );
}
